import {IDispatcher, IEvent, IListener, IListenersBag} from '@src/@interfaces/services/Events';
import Event from './Event';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';
import Subscription from './Subscription';
import {IEventDefaultPayload} from '@src/@interfaces/services/Events/IEventDefaultPayload';

export default class Dispatcher implements IDispatcher {
    protected listeners: IListenersBag<IListener<any>> = {};
    protected wildcards: IListenersBag<IListener<any>> = {};
    private wildcardsCache: IListenersBag<IListener<any>> = {};

    dispatch<TPayload = IEventDefaultPayload>(event: IEvent | string, payload: TPayload, halt = false): any | any[] | null {
        if (typeof event === 'string') {
            event = new Event(event);
        }

        const responses = [];
        const listeners = this.getListeners(event.name);
        for (let i = 0; i < listeners.length; i++) {
            const response = listeners[i](event, payload);

            if (halt && ![null, undefined].includes(response)) {
                return response;
            }

            if (response === false) {
                break;
            }

            responses.push(response);
        }

        return halt ? null : responses;
    }

    listen<TPayload>(event: string | string[], listener: IListener<TPayload>): ISubscription {
        const list = Array.isArray(event) ? event : [event];

        list.forEach(name => {
            if (name.includes('*')) {
                (this.wildcards[name] ??= []).push(listener);
            } else {
                (this.listeners[name] ??= []).push(listener);
            }
        });

        return new Subscription(this, event, listener);
    }

    listenOnce<TPayload>(event: string | string[], listener: IListener<TPayload>): ISubscription {
        const link: { subscription: ISubscription | null } = {subscription: null};

        const wrappedListener: IListener<TPayload> = (event, payload) => {
            const result = listener(event, payload);
            link.subscription?.unsubscribe();
            link.subscription = null;

            return result;
        };

        return link.subscription = this.listen(event, wrappedListener);
    }

    forget<TPayload>(event: string | string[], listener: IListener<TPayload>): void {
        const list = Array.isArray(event) ? event : [event];

        list.forEach(name => {
            const isWildcard = name.includes('*');
            const list = isWildcard ? this.wildcards : this.listeners;
            if (!(name in list)) {
                return;
            }

            let changed = false;
            for (let index = list[name].length - 1; index >= 0; index--) {
                if (list[name][index] !== listener) {
                    continue;
                }

                list[name].splice(index, 1);
                changed = true;
            }

            if (isWildcard && changed) {
                for (const key in this.wildcardsCache) {
                    if (Dispatcher.isMatches(name, key)) {
                        delete this.wildcardsCache[key];
                    }
                }
            }
        });
    }

    flush(): void {
        this.listeners = {};
        this.wildcards = {};
        this.wildcardsCache = {};
    }

    protected getListeners(eventName: string): IListener<any>[] {
        return [
            ...(this.listeners[eventName] ?? []),
            ...this.wildcardsCache[eventName] ??= this.getWildcardListeners(eventName)
        ];
    }

    protected getWildcardListeners(eventName: string): IListener<any>[] {
        return Object.keys(this.wildcards).reduce((res, key) => {
            if (Dispatcher.isMatches(key, eventName)) {
                return [
                    ...res,
                    ...this.wildcards[key]
                ];
            }

            return res;
        }, [] as IListener<any>[]);
    }

    private static isMatches(pattern: string, value: string): boolean {
        if (pattern === value) {
            return true;
        }

        pattern = pattern.replace(/([\\.+*?\[^\]$(){}=!<>|])/g, '\\$1');
        pattern = pattern.replace(/\\\*/g, '.*');

        return new RegExp(`^${pattern}$`, 'u').test(value);
    }
}
